/**
 * PURE progression selectors over `GameState.audienceProgress` — the Desk
 * roadmap's read model. Presentation layer only; no side effects, no scoring.
 *
 * Stars = crowds LANDED for a story (0..3). A story is COMPLETE at 3/3, a CLEAN
 * SWEEP when all three landed clean. A story UNLOCKS when it is the first story
 * or the prior story has >= 1 star (kept behind the soft `FOLLOWERS_FOR_STORY`
 * gate, matching the campaign's follower thresholds).
 */

import type { PackId } from "../content/registry.js";
import { SHIFT_PACK_ORDER } from "../content/registry.js";
import { FOLLOWERS_FOR_STORY, type AudienceResult, type GameState } from "./types.js";

/** The best-per-crowd results recorded for a story (empty when none). */
export function resultsForStory(
  state: GameState,
  packId: PackId,
): Record<string, AudienceResult> {
  return state.audienceProgress?.[packId] ?? {};
}

/** Number of crowds LANDED for a story (0..3). */
export function storyStars(state: GameState, packId: PackId): number {
  return Object.values(resultsForStory(state, packId)).filter((r) => r.landed).length;
}

/** A story is complete once all three crowds are landed. */
export function storyComplete(state: GameState, packId: PackId): boolean {
  return storyStars(state, packId) === 3;
}

/** A clean sweep = all three crowds landed AND landed clean. */
export function storyCleanSweep(state: GameState, packId: PackId): boolean {
  const results = Object.values(resultsForStory(state, packId));
  return results.length >= 3 && results.filter((r) => r.landed && r.clean).length >= 3;
}

/**
 * A story is unlocked when it is the first story, or the PRIOR story has >= 1
 * star. The optional soft `FOLLOWERS_FOR_STORY` follower gate still applies, so
 * a low-follower save cannot skip ahead.
 */
export function storyUnlocked(state: GameState, packId: PackId): boolean {
  const idx = SHIFT_PACK_ORDER.indexOf(packId);
  if (idx < 0) return false;
  const need = FOLLOWERS_FOR_STORY[idx] ?? 0;
  if (state.economy.followers < need) return false;
  if (idx === 0) return true;
  const prior = SHIFT_PACK_ORDER[idx - 1]!;
  return storyStars(state, prior) >= 1;
}

/** Best headline reach recorded for a story (0 when none). */
export function bestReachForStory(state: GameState, packId: PackId): number {
  return Object.values(resultsForStory(state, packId)).reduce(
    (max, r) => Math.max(max, r.reachMilli),
    0,
  );
}

/** The best recorded result for one (story, crowd), or null. */
export function bestResultForCrowd(
  state: GameState,
  packId: PackId,
  crowdId: string,
): AudienceResult | null {
  return resultsForStory(state, packId)[crowdId] ?? null;
}
